import { createMockSlugData } from '../../slug-data/mock';
import type { Technology } from '../technology';

/**
 * Realistic demo dataset of industrial {@link Technology} entries with a
 * parent → Capability leaf hierarchy. Absorbs former Service / Product demo
 * parents so the demo build and mocked e2e suite keep catalog breadth.
 * Used as the default seed in technologyHandlers.
 */
export const MOCK_TECHNOLOGIES_DEMO: Technology[] = [
  {
    id: 1,
    name: 'CNC Machining',
    slug: 'cnc-machining',
    capabilities: [
      createMockSlugData({ id: 11, name: 'Milling', slug: 'milling' }),
      createMockSlugData({ id: 12, name: 'Turning', slug: 'turning' }),
      createMockSlugData({ id: 13, name: 'Grinding', slug: 'grinding' }),
    ],
  },
  {
    id: 2,
    name: '3D Printing',
    slug: '3d-printing',
    capabilities: [
      createMockSlugData({ id: 21, name: 'FDM', slug: 'fdm' }),
      createMockSlugData({ id: 22, name: 'SLA', slug: 'sla' }),
      createMockSlugData({ id: 23, name: 'SLS', slug: 'sls' }),
    ],
  },
  {
    id: 3,
    name: 'Metal Forming',
    slug: 'metal-forming',
    capabilities: [
      createMockSlugData({ id: 31, name: 'Stamping', slug: 'stamping' }),
      createMockSlugData({
        id: 32,
        name: 'Deep Drawing',
        slug: 'deep-drawing',
      }),
      createMockSlugData({
        id: 33,
        name: 'Roll Forming',
        slug: 'roll-forming',
      }),
    ],
  },
  {
    id: 4,
    name: 'Welding',
    slug: 'welding',
    capabilities: [
      createMockSlugData({ id: 41, name: 'MIG / MAG', slug: 'mig-mag' }),
      createMockSlugData({ id: 42, name: 'TIG', slug: 'tig' }),
      createMockSlugData({
        id: 43,
        name: 'Spot Welding',
        slug: 'spot-welding',
      }),
    ],
  },
  // Absorbed from former Service demo catalog (ids 100+)
  {
    id: 5,
    name: 'Metal Fabrication',
    slug: 'metal-fabrication',
    capabilities: [
      createMockSlugData({
        id: 111,
        name: 'Laser Cutting',
        slug: 'laser-cutting',
      }),
      createMockSlugData({
        id: 112,
        name: 'Plasma Cutting',
        slug: 'plasma-cutting',
      }),
      createMockSlugData({
        id: 113,
        name: 'Water Jet Cutting',
        slug: 'water-jet-cutting',
      }),
    ],
  },
  {
    id: 6,
    name: 'Surface Treatment',
    slug: 'surface-treatment',
    capabilities: [
      createMockSlugData({
        id: 121,
        name: 'Powder Coating',
        slug: 'powder-coating',
      }),
      createMockSlugData({ id: 122, name: 'Anodizing', slug: 'anodizing' }),
      createMockSlugData({
        id: 123,
        name: 'Electroplating',
        slug: 'electroplating',
      }),
    ],
  },
  {
    id: 7,
    name: 'Assembly',
    slug: 'assembly',
    capabilities: [
      createMockSlugData({
        id: 141,
        name: 'Mechanical Assembly',
        slug: 'mechanical-assembly',
      }),
      createMockSlugData({
        id: 142,
        name: 'Electronic Assembly',
        slug: 'electronic-assembly',
      }),
    ],
  },
  // Absorbed from former Product demo catalog (ids 200+)
  {
    id: 8,
    name: 'Metal Components',
    slug: 'metal-components',
    capabilities: [
      createMockSlugData({
        id: 211,
        name: 'Brackets & Mounts',
        slug: 'brackets-mounts',
      }),
      createMockSlugData({
        id: 212,
        name: 'Shafts & Axles',
        slug: 'shafts-axles',
      }),
      createMockSlugData({
        id: 213,
        name: 'Housings & Enclosures',
        slug: 'housings-enclosures',
      }),
    ],
  },
  {
    id: 9,
    name: 'Plastic Parts',
    slug: 'plastic-parts',
    capabilities: [
      createMockSlugData({
        id: 221,
        name: 'Injection Moulded',
        slug: 'injection-moulded',
      }),
      createMockSlugData({
        id: 222,
        name: 'Blow Moulded',
        slug: 'blow-moulded',
      }),
      createMockSlugData({
        id: 223,
        name: 'Thermoformed',
        slug: 'thermoformed',
      }),
    ],
  },
  {
    id: 10,
    name: 'Electronic Components',
    slug: 'electronic-components',
    capabilities: [
      createMockSlugData({
        id: 231,
        name: 'Printed Circuit Boards',
        slug: 'pcb',
      }),
      createMockSlugData({
        id: 232,
        name: 'Wire Harnesses',
        slug: 'wire-harnesses',
      }),
    ],
  },
  {
    id: 11,
    name: 'Composite Parts',
    slug: 'composite-parts',
    capabilities: [
      createMockSlugData({
        id: 241,
        name: 'Carbon Fibre',
        slug: 'carbon-fibre',
      }),
      createMockSlugData({ id: 242, name: 'Fibreglass', slug: 'fibreglass' }),
    ],
  },
];

/** Canonical mock {@link Technology}. */
export const MOCK_TECHNOLOGY: Technology = {
  id: 1,
  name: '3D Printing',
  slug: '3d-printing',
  capabilities: [
    createMockSlugData({ id: 2, name: 'SLA', slug: 'sla' }),
    createMockSlugData({ id: 3, name: 'FDM', slug: 'fdm' }),
  ],
};

/** Builds a {@link Technology} from {@link MOCK_TECHNOLOGY} with overrides. */
export function createMockTechnology(
  overrides?: Partial<Technology>
): Technology {
  return { ...MOCK_TECHNOLOGY, ...overrides };
}

/** Builds a list of mock {@link Technology} entries with unique child ids per parent. */
export function createMockTechnologyList(
  count = 3,
  overrideAt?: (index: number) => Partial<Technology>
): Technology[] {
  return Array.from({ length: count }, (_, i) => {
    const parentId = i + 1;
    return createMockTechnology({
      id: parentId,
      name: `Technology ${parentId}`,
      slug: `technology-${parentId}`,
      capabilities: [
        createMockSlugData({
          id: parentId * 10 + 1,
          name: `Capability ${parentId}.1`,
          slug: `capability-${parentId}-1`,
        }),
        createMockSlugData({
          id: parentId * 10 + 2,
          name: `Capability ${parentId}.2`,
          slug: `capability-${parentId}-2`,
        }),
      ],
      ...overrideAt?.(i),
    });
  });
}
